import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  TextField, 
  InputAdornment,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Checkbox } from '@mui/material';
import { hasPermission } from '../common/permissions';
import api from '../../services/api';
import CsvImportDialog from './components/CsvImportDialog';

const RiskRegister = ({ onAdd, onView, user, selectedRole }) => {
  const [risks, setRisks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [inherentFilter, setInherentFilter] = useState('All');
  const [residualFilter, setResidualFilter] = useState('All');
  const [metadata, setMetadata] = useState({ campuses: [], strategic_kras: [] });
  const [loading, setLoading] = useState(true);
  
  // Table Tabs State
  const [activeTab, setActiveTab] = useState(0);

  // Edit State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [editFormData, setEditFormData] = useState({ date_reviewed: '', status: '' });

  // Delete State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [riskToDelete, setRiskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Feedback State
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState('');
  const [feedbackSeverity, setFeedbackSeverity] = useState('success');

  // Approval State
  const [selectedIds, setSelectedIds] = useState([]);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Import State
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    fetchRisks();
    fetchMetadata();
  }, []);

  const fetchMetadata = async () => {
    try {
      const response = await api.get('/risk-metadata');
      setMetadata(response.data || { campuses: [], strategic_kras: [] });
    } catch (error) {
      console.error('Error fetching risk metadata:', error);
    }
  };

  const fetchRisks = async () => {
    try {
      const response = await api.get('/risks');
      setRisks(response.data);
    } catch (error) {
      console.error('Error fetching risks:', error);
    } finally {
      setLoading(false);
    }
  };

    const getScoreLabel = (likelihood, consequence) => {
        const likelihoodMap = { 'RARE': 0, 'UNLIKELY': 1, 'POSSIBLE': 2, 'LIKELY': 3, 'ALMOST CERTAIN': 4 };
        const matrix = {
            'CATASTROPHIC': ['TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME', 'EXTREME'],
            'MAJOR': ['LOW', 'TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME'],
            'MODERATE': ['LOW', 'LOW', 'TOLERABLE', 'HIGH', 'HIGH'],
            'MINOR': ['VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE', 'HIGH'],
            'INSIGNIFICANT': ['VERY LOW', 'VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE']
        };
        
        const lKey = likelihoodMap[String(likelihood).toUpperCase()];
        const cKey = String(consequence).toUpperCase();
        
        if (matrix[cKey]) return matrix[cKey][lKey] || '---';
        
        const legacyMatrix = {
            5: ['TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME', 'EXTREME'],
            4: ['LOW', 'TOLERABLE', 'HIGH', 'EXTREME', 'EXTREME'],
            3: ['LOW', 'LOW', 'TOLERABLE', 'HIGH', 'HIGH'],
            2: ['VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE', 'HIGH'],
            1: ['VERY LOW', 'VERY LOW', 'LOW', 'TOLERABLE', 'TOLERABLE']
        };
        return legacyMatrix[consequence]?.[likelihood - 1] || '---';
    };

    const getScoreColor = (likelihood, consequence) => {
        const label = getScoreLabel(likelihood, consequence);
        const colors = {
            'EXTREME': '#ef4444',
            'HIGH': '#f97316',
            'TOLERABLE': '#eab308',
            'LOW': '#84cc16',
            'VERY LOW': '#15803d'
        };
        return colors[label] || '#94a3b8';
    };

    const getLikelihoodLabel = (val) => {
        const map = { 1: 'RARE', 2: 'UNLIKELY', 3: 'POSSIBLE', 4: 'LIKELY', 5: 'ALMOST CERTAIN' };
        return map[val] || val;
    };

    const getConsequenceLabel = (val) => {
        const map = { 1: 'INSIGNIFICANT', 2: 'MINOR', 3: 'MODERATE', 4: 'MAJOR', 5: 'CATASTROPHIC' };
        return map[val] || val;
    };

  const categories = ['All', ...new Set(risks.map(r => r.category).filter(Boolean).sort())];
  const ratings = ['All', 'EXTREME', 'HIGH', 'TOLERABLE', 'LOW', 'VERY LOW'];

  const filteredRisks = risks.filter(risk => {
    const matchesSearch = 
      risk.risk_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      risk.sn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      risk.owner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      risk.department_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      risk.sub_department_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      risk.campus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      risk.strategic_kra_names?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'All' || risk.category === categoryFilter;
    const matchesInherent = inherentFilter === 'All' || getScoreLabel(risk.inherent_likelihood, risk.inherent_consequence) === inherentFilter;
    const matchesResidual = residualFilter === 'All' || getScoreLabel(risk.residual_likelihood, risk.residual_consequence) === residualFilter;

    return matchesSearch && matchesCategory && matchesInherent && matchesResidual;
  });

  const canAdd = hasPermission(user, 'Risk Registers', 'add');
  const canEdit = hasPermission(user, 'Risk Registers', 'edit');
  const canDelete = hasPermission(user, 'Risk Registers', 'delete');
  const isHOD = hasPermission(user, 'Risk Registers', 'approve');

  const pendingRisks = filteredRisks.filter(r => r.approval_status === 'pending' || !r.approval_status);

  const handleSelectAll = (checked) => {
    setSelectedIds(checked ? pendingRisks.map(r => r.id) : []);
  };

  const handleSelectOne = (id, checked) => {
    setSelectedIds(prev => checked ? [...prev, id] : prev.filter(x => x !== id));
  };

  const handleBulkApprove = async () => {
    if (!selectedIds.length) return;
    setApprovalLoading(true);
    try {
      await api.post('/risks/approve', { ids: selectedIds });
      setFeedbackMsg(`${selectedIds.length} risk(s) approved successfully.`);
      setFeedbackSeverity('success');
      setSelectedIds([]);
      fetchRisks();
    } catch (e) {
      setFeedbackMsg(e.response?.data?.message || 'Approval failed.');
      setFeedbackSeverity('error');
    } finally {
      setApprovalLoading(false);
      setFeedbackOpen(true);
    }
  };

  const handleBulkReject = async () => {
    if (!rejectReason.trim()) return;
    setApprovalLoading(true);
    try {
      await api.post('/risks/reject', { ids: selectedIds, reason: rejectReason });
      setFeedbackMsg(`${selectedIds.length} risk(s) rejected.`);
      setFeedbackSeverity('warning');
      setSelectedIds([]);
      setRejectReason('');
      setRejectDialogOpen(false);
      fetchRisks();
    } catch (e) {
      setFeedbackMsg(e.response?.data?.message || 'Rejection failed.');
      setFeedbackSeverity('error');
    } finally {
      setApprovalLoading(false);
      setFeedbackOpen(true);
    }
  };

  const getApprovalChip = (status) => {
    const map = {
      hod_approved: { label: 'Approved', color: '#10b981', bg: 'rgba(16,185,129,0.1)' },
      rejected:     { label: 'Rejected', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' },
      pending:      { label: 'Pending',  color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' },
    };
    const s = map[status] || map.pending;
    return <Chip label={s.label} size="small" sx={{ color: s.color, background: s.bg, border: `1px solid ${s.color}40`, fontWeight: 'bold', fontSize: '0.7rem' }} />;
  };

  const handleEditClick = (risk) => {
    setEditingRisk(risk);
    setEditFormData({
        date_reviewed: risk.date_reviewed || new Date().toISOString().split('T')[0],
        status: risk.status || 'Open',
        campus: risk.campus || '',
        strategic_kra_ids: risk.strategic_kra_ids || []
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (editingRisk.date_reviewed && editFormData.date_reviewed < editingRisk.date_reviewed) {
        setFeedbackMsg(`New review date cannot be before the previous review date (${editingRisk.date_reviewed})`);
        setFeedbackSeverity('error');
        setFeedbackOpen(true);
        return;
    }

    try {
        await api.post('/risks/update', {
            id: editingRisk.id,
            ...editFormData
        });

        setFeedbackMsg('Risk record updated successfully!');
        setFeedbackSeverity('success');
        setEditDialogOpen(false);
        fetchRisks();
    } catch (err) {
        if (err.response) {
            setFeedbackMsg(err.response.data.error || 'Failed to update risk record.');
        } else {
            setFeedbackMsg('Error connecting to the server.');
        }
        setFeedbackSeverity('error');
    } finally {
        setFeedbackOpen(true);
    }
  };

  const handleDeleteClick = (risk) => {
    setRiskToDelete(risk);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!riskToDelete) return;
    setDeleting(true);
    try {
        await api.delete(`/risks/${riskToDelete.id}`);
        setFeedbackMsg('Risk record deleted successfully.');
        setFeedbackSeverity('success');
        setDeleteDialogOpen(false);
        setRiskToDelete(null);
        fetchRisks();
    } catch (err) {
        setFeedbackMsg(err.response?.data?.error || 'Failed to delete risk record.');
        setFeedbackSeverity('error');
    } finally {
        setDeleting(false);
        setFeedbackOpen(true);
    }
  };

  // Sticky Column Styles
  const stickyColumnStyle = {
    position: 'sticky',
    left: 0,
    background: '#0f172a', 
    zIndex: 10,
    borderRight: '1px solid rgba(255,255,255,0.1) !important'
  };

  const stickyHeaderStyle = {
    ...stickyColumnStyle,
    zIndex: 11,
    background: '#1e293b' // Slightly lighter for the header
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" className="gradient-text" sx={{ fontWeight: 'bold' }}>
          Risk Register
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search risks..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              width: 300,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                background: 'rgba(255,255,255,0.05)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
              }
            }}
          />
          {canAdd && (
            <Button
                variant="outlined"
                onClick={() => setImportOpen(true)}
                sx={{
                    color: '#3b82f6', borderColor: 'rgba(59,130,246,0.5)',
                    textTransform: 'none', fontWeight: 'bold', px: 3,
                    borderRadius: '12px',
                    '&:hover': { background: 'rgba(59,130,246,0.08)' }
                }}
            >
                Import CSV
            </Button>
          )}
          {canAdd && (
            <Button 
                variant="contained" 
                onClick={onAdd}
                sx={{ 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    px: 3,
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}
            >
                Add New Risk
            </Button>
          )}
        </Box>
      </Box>

      {/* Advanced Filters */}
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        mb: 2, 
        flexWrap: 'wrap', 
        p: 2.5, 
        background: 'rgba(15, 23, 42, 0.4)', 
        borderRadius: '20px', 
        border: '1px solid rgba(255,255,255,0.08)',
        alignItems: 'center'
      }}>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 'bold', mr: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            FILTER BY:
        </Typography>

        <TextField
            select
            size="small"
            label="Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ 
                minWidth: 200, 
                '& .MuiSelect-select': { color: 'white', fontSize: '0.8rem' },
                '& .MuiInputLabel-root': { color: '#64748b', fontSize: '0.8rem' },
                '& .MuiOutlinedInput-root': {
                    background: 'rgba(255,255,255,0.03)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.05)' },
                }
            }}
            SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' } } } }}
        >
            {categories.map(cat => <MenuItem key={cat} value={cat} sx={{ fontSize: '0.8rem' }}>{cat}</MenuItem>)}
        </TextField>

        <TextField
            select
            size="small"
            label="Inherent Rating"
            value={inherentFilter}
            onChange={(e) => setInherentFilter(e.target.value)}
            sx={{ 
                minWidth: 160, 
                '& .MuiSelect-select': { color: 'white', fontSize: '0.8rem' },
                '& .MuiInputLabel-root': { color: '#64748b', fontSize: '0.8rem' },
                '& .MuiOutlinedInput-root': {
                    background: 'rgba(255,255,255,0.03)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.05)' },
                }
            }}
            SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' } } } }}
        >
            {ratings.map(r => <MenuItem key={r} value={r} sx={{ fontSize: '0.8rem' }}>{r}</MenuItem>)}
        </TextField>

        <TextField
            select
            size="small"
            label="Residual Rating"
            value={residualFilter}
            onChange={(e) => setResidualFilter(e.target.value)}
            sx={{ 
                minWidth: 160, 
                '& .MuiSelect-select': { color: 'white', fontSize: '0.8rem' },
                '& .MuiInputLabel-root': { color: '#64748b', fontSize: '0.8rem' },
                '& .MuiOutlinedInput-root': {
                    background: 'rgba(255,255,255,0.03)',
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.05)' },
                }
            }}
            SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white', border: '1px solid rgba(255,255,255,0.1)' } } } }}
        >
            {ratings.map(r => <MenuItem key={r} value={r} sx={{ fontSize: '0.8rem' }}>{r}</MenuItem>)}
        </TextField>

        {(categoryFilter !== 'All' || inherentFilter !== 'All' || residualFilter !== 'All' || searchTerm) && (
            <Button 
                size="small"
                onClick={() => { setCategoryFilter('All'); setInherentFilter('All'); setResidualFilter('All'); setSearchTerm(''); }}
                sx={{ 
                    color: '#ef4444', 
                    textTransform: 'none', 
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    ml: 'auto',
                    '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } 
                }}
            >
                Reset All Filters
            </Button>
        )}
      </Box>

      {/* Table Tabs - split into scrollable left + pinned right */}
      <Box sx={{ mb: 3, borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'flex-end' }}>
        {/* Scrollable tabs (left) */}
        <Box sx={{ flex: 1, overflow: 'hidden', minWidth: 0 }}>
          <Tabs 
            value={activeTab <= 4 ? activeTab : false} 
            onChange={(e, val) => setActiveTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: '48px',
              '& .MuiTab-root': {
                color: '#94a3b8',
                textTransform: 'none',
                fontWeight: 'bold',
                fontSize: '0.85rem',
                minWidth: 'auto',
                px: 3,
                transition: 'all 0.3s ease',
                '&.Mui-selected': { 
                  color: 'white !important', 
                  background: 'rgba(59, 130, 246, 0.15)',
                  borderRadius: '8px 8px 0 0'
                },
                '&:hover': { color: '#f8fafc' }
              },
              '& .MuiTabs-indicator': { backgroundColor: '#3b82f6', height: '3px' }
            }}
          >
            <Tab label="Risk Identification" />
            <Tab label="Risk Assessment (Inherent)" />
            <Tab label="Risk Assessment (Residual)" />
            <Tab label="Risk Treatment" />
            <Tab label="Risk Monitoring & Review" />
          </Tabs>
        </Box>

        {/* Pinned tabs (right) - always visible */}
        <Box sx={{ display: 'flex', flexShrink: 0, borderLeft: '1px solid rgba(255,255,255,0.1)', ml: 1 }}>
          <Box 
            onClick={() => setActiveTab(5)} 
            sx={{ 
              px: 3, py: 1.5, cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem',
              color: activeTab === 5 ? 'white' : '#94a3b8',
              background: activeTab === 5 ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
              borderRadius: '8px 8px 0 0',
              borderBottom: activeTab === 5 ? '3px solid #3b82f6' : '3px solid transparent',
              transition: 'all 0.3s ease',
              '&:hover': { color: '#f8fafc' }
            }}
          >
            Actions
          </Box>
          {isHOD && (
            <Box 
              onClick={() => setActiveTab(6)} 
              sx={{ 
                px: 3, py: 1.5, cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem',
                color: activeTab === 6 ? 'white' : '#f59e0b',
                background: activeTab === 6 ? 'rgba(245, 158, 11, 0.15)' : 'transparent',
                borderRadius: '8px 8px 0 0',
                borderBottom: activeTab === 6 ? '3px solid #f59e0b' : '3px solid transparent',
                transition: 'all 0.3s ease',
                '&:hover': { color: '#fbbf24' }
              }}
            >
              {`HOD Approval${pendingRisks.length ? ` (${pendingRisks.length})` : ''}`}
            </Box>
          )}
        </Box>
      </Box>

      {activeTab === 6 && isHOD && selectedIds.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, p: 2, background: 'rgba(245,158,11,0.08)', borderRadius: '12px', border: '1px solid rgba(245,158,11,0.2)' }}>
          <Typography sx={{ color: '#f59e0b', fontWeight: 'bold', fontSize: '0.85rem' }}>{selectedIds.length} risk(s) selected</Typography>
          <Button size="small" variant="contained" startIcon={approvalLoading ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />} onClick={handleBulkApprove} disabled={approvalLoading} sx={{ background: '#10b981', textTransform: 'none', fontWeight: 'bold', borderRadius: '8px', '&:hover': { background: '#059669' } }}>Approve All Selected</Button>
          <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={() => setRejectDialogOpen(true)} disabled={approvalLoading} sx={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.5)', textTransform: 'none', fontWeight: 'bold', borderRadius: '8px' }}>Reject All Selected</Button>
        </Box>
      )}

      <TableContainer component={Paper} sx={{ background: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(30px)', boxShadow: 'none', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)', overflow: 'auto' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { background: '#1e293b', borderBottom: '2px solid rgba(255,255,255,0.1)' } }}>
              <TableCell sx={{ ...stickyHeaderStyle, color: '#3b82f6', fontWeight: '900', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Risk ID</TableCell>
              
              {activeTab === 0 && (
                <>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Date Reviewed</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Category</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Campus</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Strategic KRA</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Cause</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Consequence</TableCell>
                </>
              )}

              {activeTab === 1 && (
                <>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }} align="center">Likelihood</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }} align="center">Consequence</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }} align="center">Rating</TableCell>
                </>
              )}

              {activeTab === 2 && (
                <>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Existing Controls</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Adequacy</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }} align="center">Likelihood</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }} align="center">Consequence</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }} align="center">Rating</TableCell>
                </>
              )}

              {activeTab === 3 && (
                <>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Mitigation Strategy</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Action / Treatment</TableCell>
                </>
              )}

              {activeTab === 4 && (
                <>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Risk Owner</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>TARGET RESOLUTION</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Method</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Status</TableCell>
                </>
              )}

              {activeTab === 5 && (
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }} align="center">Actions</TableCell>
              )}

              {activeTab === 6 && isHOD && (
                <>
                  <TableCell padding="checkbox"><Checkbox checked={selectedIds.length === pendingRisks.length && pendingRisks.length > 0} indeterminate={selectedIds.length > 0 && selectedIds.length < pendingRisks.length} onChange={(e) => handleSelectAll(e.target.checked)} sx={{ color: '#94a3b8' }} /></TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Description</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Department</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Owner</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }}>Approval Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.75rem' }} align="center">Action</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
                <TableRow>
                    <TableCell colSpan={activeTab === 0 ? 8 : activeTab === 2 ? 6 : 4} align="center" sx={{ py: 10 }}>
                        <CircularProgress sx={{ color: '#10b981' }} />
                    </TableCell>
                </TableRow>
            ) : activeTab === 6 && isHOD ? filteredRisks.map((risk) => (
              <TableRow key={risk.id} sx={{ '&:hover': { background: 'rgba(255,255,255,0.02)' }, '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 1.5, fontSize: '0.85rem' } }}>
                <TableCell sx={{ ...stickyColumnStyle, fontWeight: 'bold', color: '#10b981' }}>{risk.sn}</TableCell>
                <TableCell padding="checkbox"><Checkbox checked={selectedIds.includes(risk.id)} onChange={(e) => handleSelectOne(risk.id, e.target.checked)} disabled={risk.approval_status === 'hod_approved' || risk.approval_status === 'rejected'} sx={{ color: '#94a3b8' }} /></TableCell>
                <TableCell sx={{ color: '#f1f5f9', maxWidth: 280 }}>{risk.risk_description}</TableCell>
                <TableCell sx={{ color: '#f1f5f9' }}>
                    {risk.department_name || '---'}
                    {risk.sub_department_name && <span style={{ color: '#64748b', fontSize: '0.75rem', display: 'block' }}>{risk.sub_department_name}</span>}
                  </TableCell>
                <TableCell sx={{ color: '#f1f5f9' }}>{risk.owner}</TableCell>
                <TableCell>{getApprovalChip(risk.approval_status)}</TableCell>
                <TableCell align="center">
                  {(risk.approval_status === 'pending' || !risk.approval_status) && (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button size="small" variant="outlined" startIcon={<CheckCircleIcon />} onClick={() => { setSelectedIds([risk.id]); handleBulkApprove(); }} sx={{ color: '#10b981', borderColor: 'rgba(16,185,129,0.5)', textTransform: 'none', fontSize: '0.7rem', borderRadius: '8px', '&:hover': { background: 'rgba(16,185,129,0.1)' } }}>Approve</Button>
                      <Button size="small" variant="outlined" startIcon={<CancelIcon />} onClick={() => { setSelectedIds([risk.id]); setRejectDialogOpen(true); }} sx={{ color: '#ef4444', borderColor: 'rgba(239,68,68,0.5)', textTransform: 'none', fontSize: '0.7rem', borderRadius: '8px', '&:hover': { background: 'rgba(239,68,68,0.1)' } }}>Reject</Button>
                    </Box>
                  )}
                  {risk.approval_status === 'rejected' && risk.rejection_reason && (
                    <Typography sx={{ color: '#f87171', fontSize: '0.7rem', fontStyle: 'italic', maxWidth: 180 }}>{risk.rejection_reason}</Typography>
                  )}
                </TableCell>
              </TableRow>
            )) : filteredRisks.map((risk) => (
              <TableRow 
                key={risk.id}
                sx={{ 
                  '&:hover': { background: 'rgba(255,255,255,0.02)' },
                  '& td': { borderColor: 'rgba(255,255,255,0.05)', py: 2, fontSize: '0.85rem' }
                }}
              >
                <TableCell sx={{ ...stickyColumnStyle, fontWeight: 'bold', color: '#10b981' }}>{risk.sn}</TableCell>
                
                {activeTab === 0 && (
                  <>
                    <TableCell sx={{ color: '#f1f5f9' }}>{risk.date_reviewed || '---'}</TableCell>
                    <TableCell sx={{ color: '#f1f5f9' }}>{risk.category}</TableCell>
                    <TableCell sx={{ color: '#f1f5f9' }}>{risk.campus || '---'}</TableCell>
                    <TableCell sx={{ maxWidth: 350, color: '#f8fafc' }}>{risk.risk_description}</TableCell>
                    <TableCell sx={{ maxWidth: 260, color: '#f1f5f9' }}>{risk.strategic_kra_names || risk.kra_at_risk || '---'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, color: '#f1f5f9' }}>{risk.causes || '---'}</TableCell>
                    <TableCell sx={{ maxWidth: 200, color: '#f1f5f9' }}>{risk.consequence || '---'}</TableCell>
                  </>
                )}

                {activeTab === 1 && (
                  <>
                    <TableCell sx={{ color: '#f1f5f9', textAlign: 'center', fontSize: '0.75rem' }}>{getLikelihoodLabel(risk.inherent_likelihood)}</TableCell>
                    <TableCell sx={{ color: '#f1f5f9', textAlign: 'center', fontSize: '0.75rem' }}>{getConsequenceLabel(risk.inherent_consequence)}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={getScoreLabel(risk.inherent_likelihood, risk.inherent_consequence)} 
                        size="small"
                        sx={{ 
                          background: `${getScoreColor(risk.inherent_likelihood, risk.inherent_consequence)}15`,
                          color: getScoreColor(risk.inherent_likelihood, risk.inherent_consequence),
                          border: `1px solid ${getScoreColor(risk.inherent_likelihood, risk.inherent_consequence)}40`,
                          fontWeight: 'bold', fontSize: '0.7rem', width: '100px'
                        }} 
                      />
                    </TableCell>
                  </>
                )}

                {activeTab === 2 && (
                  <>
                    <TableCell sx={{ maxWidth: 300, color: '#f1f5f9' }}>
                      <Box>
                        {risk.existing_controls && (
                          <Typography variant="body2" sx={{ mb: risk.controls?.length > 0 ? 1 : 0 }}>
                            {risk.existing_controls}
                          </Typography>
                        )}
                        {risk.controls && risk.controls.length > 0 && risk.controls.map((c, idx) => (
                          <Typography key={idx} variant="caption" sx={{ display: 'block', color: '#10b981', mt: 0.5, fontStyle: 'italic', lineHeight: 1.2 }}>
                            • {c.control_measure}
                          </Typography>
                        ))}
                        {!risk.existing_controls && (!risk.controls || risk.controls.length === 0) && '---'}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#f1f5f9' }}>{risk.control_effectiveness || '---'}</TableCell>
                    <TableCell sx={{ color: '#f1f5f9', textAlign: 'center', fontSize: '0.75rem' }}>{getLikelihoodLabel(risk.residual_likelihood)}</TableCell>
                    <TableCell sx={{ color: '#f1f5f9', textAlign: 'center', fontSize: '0.75rem' }}>{getConsequenceLabel(risk.residual_consequence)}</TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={getScoreLabel(risk.residual_likelihood, risk.residual_consequence)} 
                        size="small"
                        sx={{ 
                          background: `${getScoreColor(risk.residual_likelihood, risk.residual_consequence)}15`,
                          color: getScoreColor(risk.residual_likelihood, risk.residual_consequence),
                          border: `1px solid ${getScoreColor(risk.residual_likelihood, risk.residual_consequence)}40`,
                          fontWeight: 'bold', fontSize: '0.7rem', width: '100px'
                        }} 
                      />
                    </TableCell>
                  </>
                )}

                {activeTab === 3 && (
                  <>
                    <TableCell sx={{ maxWidth: 350, color: '#f1f5f9' }}>{risk.mitigation_strategy || '---'}</TableCell>
                    <TableCell sx={{ maxWidth: 350, color: '#f1f5f9' }}>{risk.action_treatment || '---'}</TableCell>
                  </>
                )}

                {activeTab === 4 && (
                  <>
                    <TableCell sx={{ color: '#f1f5f9' }}>{risk.owner}</TableCell>
                    <TableCell sx={{ color: '#f1f5f9' }}>{risk.resolved_by || '---'}</TableCell>
                    <TableCell sx={{ color: '#f1f5f9' }}>{risk.method || '---'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={risk.status} 
                        size="small"
                        sx={{ 
                          background: risk.status === 'Open' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(255,255,255,0.05)',
                          color: risk.status === 'Open' ? '#10b981' : '#94a3b8',
                          border: `1px solid ${risk.status === 'Open' ? '#10b98140' : 'rgba(255,255,255,0.1)'}`,
                          fontWeight: 'bold', fontSize: '0.7rem'
                        }} 
                      />
                    </TableCell>
                  </>
                )}

                {activeTab === 5 && (
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Button 
                        variant="outlined" 
                        size="small"
                        onClick={() => onView(risk)}
                        sx={{ 
                            color: '#3b82f6', borderColor: 'rgba(59, 130, 246, 0.5)',
                            textTransform: 'none', fontSize: '0.75rem', fontWeight: 'bold',
                            borderRadius: '8px', minWidth: '70px',
                            '&:hover': { borderColor: '#3b82f6', background: 'rgba(59, 130, 246, 0.1)' }
                        }}
                      >
                        View
                      </Button>
                      {canEdit && (
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => handleEditClick(risk)}
                          sx={{ 
                              color: '#10b981', borderColor: 'rgba(16, 185, 129, 0.5)',
                              textTransform: 'none', fontSize: '0.75rem', fontWeight: 'bold',
                              borderRadius: '8px', minWidth: '70px',
                              '&:hover': { borderColor: '#10b981', background: 'rgba(16, 185, 129, 0.1)' }
                          }}
                        >
                          Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button 
                          variant="outlined" 
                          size="small"
                          onClick={() => handleDeleteClick(risk)}
                          sx={{ 
                              color: '#ef4444', borderColor: 'rgba(239, 68, 68, 0.5)',
                              textTransform: 'none', fontSize: '0.75rem', fontWeight: 'bold',
                              borderRadius: '8px', minWidth: '70px',
                              '&:hover': { borderColor: '#ef4444', background: 'rgba(239, 68, 68, 0.1)' }
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!loading && filteredRisks.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 10 }}>
                  <Typography variant="h6" sx={{ color: '#64748b' }}>
                    No risk records found matching your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Risk Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        PaperProps={{
            sx: {
                background: '#1e293b',
                color: 'white',
                borderRadius: '24px',
                border: '1px solid rgba(255,255,255,0.1)',
                minWidth: '400px',
                p: 2
            }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.2rem', pb: 1 }}>Update Risk Record</DialogTitle>
        <DialogContent>
            <Typography variant="caption" sx={{ color: '#94a3b8', mb: 3, display: 'block' }}>
                RE: {editingRisk?.sn} - {editingRisk?.risk_description?.substring(0, 50)}...
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                    fullWidth
                    type="date"
                    label="Date Reviewed"
                    value={editFormData.date_reviewed}
                    onChange={(e) => setEditFormData({ ...editFormData, date_reviewed: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: new Date().toISOString().split('T')[0] }}
                    sx={{ 
                        input: { color: 'white' }, 
                        label: { color: '#94a3b8' },
                        '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255,255,255,0.1)' }
                    }}
                />
                <TextField
                    select
                    fullWidth
                    required
                    label="Campus"
                    value={editFormData.campus}
                    onChange={(e) => setEditFormData({ ...editFormData, campus: e.target.value })}
                    sx={{ 
                        '& .MuiSelect-select': { color: 'white' }, 
                        label: { color: '#94a3b8' },
                        '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255,255,255,0.1)' }
                    }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white' } } } }}
                >
                    {(metadata.campuses || []).map(campus => <MenuItem key={campus} value={campus}>{campus}</MenuItem>)}
                </TextField>
                <Autocomplete
                    multiple
                    options={metadata.strategic_kras || []}
                    getOptionLabel={(option) => option?.name ? `${option.code} - ${option.name}` : ''}
                    value={(metadata.strategic_kras || []).filter(kra => (editFormData.strategic_kra_ids || []).includes(kra.id))}
                    onChange={(e, val) => setEditFormData({ ...editFormData, strategic_kra_ids: val.map(kra => kra.id) })}
                    renderInput={(params) => (
                        <TextField {...params} label="Strategic KRA" sx={{ input: { color: 'white' }, label: { color: '#94a3b8' }, '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255,255,255,0.1)' } }} />
                    )}
                />
                <TextField
                    select
                    fullWidth
                    label="Status"
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    sx={{ 
                        '& .MuiSelect-select': { color: 'white' }, 
                        label: { color: '#94a3b8' },
                        '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255,255,255,0.1)' }
                    }}
                    SelectProps={{ MenuProps: { PaperProps: { sx: { bgcolor: '#1e293b', color: 'white' } } } }}
                >
                    <MenuItem value="Open">Open</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Resolved">Resolved</MenuItem>
                    <MenuItem value="Closed">Closed</MenuItem>
                </TextField>
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
            <Button 
                variant="contained" 
                onClick={handleSaveEdit}
                sx={{ 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    borderRadius: '12px',
                    px: 3
                }}
            >
                Save Changes
            </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Risk Confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: '#1e293b',
            color: 'white',
            borderRadius: '16px',
            border: '1px solid rgba(255,255,255,0.1)',
            padding: '16px'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#ef4444' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#94a3b8' }}>
            Are you sure you want to delete this risk record? This action cannot be undone.
          </Typography>
          {riskToDelete && (
            <Box sx={{ mt: 2, p: 2, background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
              <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 'bold' }}>{riskToDelete.sn}</Typography>
              <Typography variant="body2" sx={{ color: '#f1f5f9' }}>{riskToDelete.risk_description}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            disabled={deleting}
            variant="contained" 
            sx={{ background: '#ef4444', textTransform: 'none', '&:hover': { background: '#dc2626' } }}
          >
            {deleting ? 'Deleting...' : 'Delete Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Reason Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} PaperProps={{ sx: { background: '#1e293b', color: 'white', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)', minWidth: '420px' } }}>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#ef4444' }}>Reject Risk(s) — Provide Reason</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#94a3b8', mb: 2, fontSize: '0.85rem' }}>Rejection reason will be recorded against the selected risk(s) and logged.</Typography>
          <TextField fullWidth multiline rows={3} label="Rejection Reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} sx={{ textarea: { color: 'white' }, label: { color: '#94a3b8' }, '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(255,255,255,0.15)' } }} />
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={() => { setRejectDialogOpen(false); setRejectReason(''); }} sx={{ color: '#94a3b8', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleBulkReject} disabled={!rejectReason.trim() || approvalLoading} variant="contained" sx={{ background: '#ef4444', textTransform: 'none', fontWeight: 'bold', borderRadius: '8px', '&:hover': { background: '#dc2626' } }}>
            {approvalLoading ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback Dialog */}
      <Dialog 
        open={feedbackOpen} 
        onClose={() => setFeedbackOpen(false)}
        PaperProps={{
            sx: {
                background: '#1e293b',
                color: 'white',
                borderRadius: '20px',
                border: '1px solid rgba(255,255,255,0.1)',
                minWidth: '300px'
            }
        }}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert 
                severity={feedbackSeverity} 
                variant="filled" 
                sx={{ 
                    mb: 3, 
                    justifyContent: 'center',
                    borderRadius: '12px',
                    background: feedbackSeverity === 'success' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                }}
            >
                {feedbackSeverity === 'success' ? 'SUCCESS' : 'ERROR'}
            </Alert>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                {feedbackSeverity === 'success' ? 'Record Updated' : 'Update Failed'}
            </Typography>
            <Typography sx={{ color: '#94a3b8', mb: 4 }}>{feedbackMsg}</Typography>
            <Button 
                fullWidth 
                variant="outlined" 
                onClick={() => setFeedbackOpen(false)}
                sx={{ 
                    borderColor: 'rgba(255,255,255,0.1)', 
                    color: 'white',
                    borderRadius: '12px',
                    '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.05)' }
                }}
            >
                Close
            </Button>
        </Box>
      </Dialog>

      <CsvImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        type="risks"
        onSuccess={() => { fetchRisks(); setImportOpen(false); }}
      />
    </Box>
  );
};

export default RiskRegister;
